import React from 'react';
import { Linkedin, Quote } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ImageFallback } from '@/components/ui';

/**
 * Fallback "About / Team" page, shown until an admin publishes a CMS page with
 * the `team` slug. Same people and story as before; restyled onto the shared
 * card/typography primitives, and without the old navbar scroll handler, which
 * looked up a `#navbar` element that no longer exists and threw on every
 * scroll event.
 */
const TEAM = [
  {
    name: 'Aadil Bhati',
    role: 'Founder & CEO',
    linkedin: 'https://www.linkedin.com/in/aadilbhatibkn/',
    bio: 'Local Konnect is founded by Aadil Bhati, with a vision to digitally transform the local economy of tier-2 and tier-3 cities starting from Bikaner.',
  },
  { name: 'Pinky Boora', role: 'HR Head' },
  { name: 'Abhay Mishra', role: 'Chief Technical Officer' },
  {
    name: 'Muhammed Altamush',
    role: 'Business Development Manager',
    linkedin: 'https://www.linkedin.com/in/muhammed-altamush-ab6649241/',
  },
  {
    name: 'Samar Bhati',
    role: 'Web Developer',
    linkedin: 'https://www.linkedin.com/in/samar-bhati-4aa2a7298/',
  },
];

const Team = () => (
  <div className="flex min-h-screen flex-col bg-surface-muted">
    <Header />
    <main className="flex-1">
      <section className="border-b border-ink-200/70 bg-white">
        <div className="lk-container py-12 md:py-20">
          <p className="lk-eyebrow mb-3">Who we are</p>
          <h1 className="text-display max-w-3xl text-ink-900">The team behind Local Konnect</h1>

          <div className="mt-8 grid gap-6 text-base leading-relaxed text-ink-700 md:mt-10 md:grid-cols-2 md:gap-12 md:text-lg">
            <p className="text-lg font-medium text-ink-900 md:text-xl">
              Local Konnect started as a simple idea to help family and friends find trusted local
              services.
            </p>
            <div className="space-y-4">
              <p>
                We began by building a small tool to book everyday services like salons, deliveries,
                and repairs — but we quickly realized the challenge people face in finding reliable
                service providers nearby. That insight fueled our growth.
              </p>
              <p>
                So we transformed Local Konnect into a smart, intuitive platform — where anyone can
                discover, book, and trust local services with just a few taps. We make local living
                easier by connecting real people with real services, while letting providers take
                full control of their business.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="lk-container py-12 md:py-16">
        <h2 className="lk-heading mb-6">Meet the team</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map((person, i) => (
            <article
              key={person.name}
              className="lk-card flex animate-floatUp gap-4 p-5"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <ImageFallback label={person.name} className="h-14 w-14 shrink-0 rounded-2xl" />
              <div className="min-w-0">
                <h3 className="text-h3 text-ink-900">{person.name}</h3>
                <p className="text-sm text-ink-500">{person.role}</p>
                {person.bio && <p className="mt-3 text-sm leading-relaxed text-ink-700">{person.bio}</p>}
                {person.linkedin && (
                  <a
                    href={person.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lk-focus mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg text-sm font-semibold text-brand-secondary hover:text-brand-blueDark"
                  >
                    <Linkedin className="h-4 w-4" aria-hidden="true" />
                    LinkedIn
                    <span className="sr-only"> profile of {person.name}</span>
                  </a>
                )}
              </div>
            </article>
          ))}

          <figure className="flex flex-col justify-between gap-4 rounded-2xl bg-brand-tint p-6">
            <Quote className="h-6 w-6 text-brand-secondary" aria-hidden="true" />
            <blockquote className="text-lg font-semibold leading-snug text-ink-900">
              &ldquo;The only way to do great work is to love what you do.&rdquo;
            </blockquote>
            <figcaption className="text-sm text-ink-500">Steve Jobs</figcaption>
          </figure>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Team;
